import React, { useCallback, useEffect, useState } from "react";
import useCompanyDateFormat from "../../hooks/useCompanyDateFormat";
import {
  Table, Card, Row, Col, Statistic, Space, Tag, Button, Input, Select, Modal,
  message, Typography, Popconfirm, Tooltip, Timeline,
} from "antd";
import { FilePdfOutlined, HistoryOutlined, StopOutlined, UnlockOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import moment from "moment";
import Service from "../../service";
import "./ReviewForms.css";
import { TABLE_PAGINATION, DEFAULT_PAGE_SIZE } from "../../constants/tablePagination";

const { Text } = Typography;
const { TextArea } = Input;

const MILESTONES = [
  { value: "WIL", label: "What I Learnt" },
  { value: "THREE_MONTH", label: "3 Month" },
  { value: "SIX_MONTH", label: "6 Month" },
  { value: "ONE_YEAR", label: "1 Year" },
];

const STATUSES = [
  { value: "PENDING_UPLOAD", label: "Pending Upload" },
  { value: "SUBMITTED", label: "Awaiting Review" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "WAIVED", label: "Waived" },
];

const STATUS_COLOR = {
  PENDING_UPLOAD: "orange",
  SUBMITTED: "blue",
  APPROVED: "green",
  REJECTED: "red",
  WAIVED: "default",
};

/**
 * HR/Admin view of every milestone review form in the company.
 *
 * The count tiles double as filters. "Overdue" is computed from the rows on the
 * current page rather than company-wide, so it is labelled as a page figure -
 * claiming a company-wide number the query did not produce would be worse than
 * saying less.
 */
const ReviewFormTracker = () => {
  const dateFormat = useCompanyDateFormat();
  const [data, setData] = useState({ rows: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ milestone: null, status: null, search: "" });
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [history, setHistory] = useState(null);
  const [waiving, setWaiving] = useState(null);
  const [waiveReason, setWaiveReason] = useState("");
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.current,
        limit: pagination.pageSize,
      });
      if (filters.milestone) params.append("milestone", filters.milestone);
      if (filters.status) params.append("status", filters.status);
      if (filters.search) params.append("search", filters.search);

      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: `${Service.reviewFormList}?${params.toString()}`,
      });
      if (response?.data?.status === 200) {
        setData(response.data.data);
      } else {
        message.error(response?.data?.message || "Could not load review forms");
      }
    } catch (error) {
      message.error("Could not load review forms");
    } finally {
      setLoading(false);
    }
  }, [filters, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (api, row, body = {}, successMessage) => {
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: `${api}/${row._id}`,
        body,
      });
      if (response?.data?.status === 200) {
        message.success(successMessage || response.data.message);
        load();
      } else {
        message.error(response?.data?.message || "Action failed");
      }
    } catch (error) {
      message.error("Action failed");
    }
  };

  /** Run the assignment sweep for this company, dry first so HR sees the blast radius. */
  const runAssignment = async (dryRun) => {
    try {
      setRunning(true);
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.reviewFormRunAssignment,
        body: { dryRun },
      });
      if (response?.data?.status === 200) {
        const summary = response.data.data?.summaries?.[0] || {};
        Modal.info({
          title: dryRun ? "Dry run: nothing was written" : "Assignment run complete",
          content: (
            <Space direction="vertical">
              <Text>
                {dryRun ? "Would create" : "Created"}: <strong>{summary.created || 0}</strong>
              </Text>
              <Text>
                Already overdue on arrival: <strong>{summary.overdueOnArrival || 0}</strong>
              </Text>
              <Text type="secondary">
                Skipped, window not open: {summary.skippedWindowNotOpen || 0} · no template:{" "}
                {summary.skippedNoTemplate || 0} · already assigned: {summary.skippedExisting || 0}
              </Text>
              {!!summary.skippedNoTemplate && (
                <Text type="warning">
                  {summary.skippedNoTemplate} milestone(s) have no matching Document Master
                  template and were not assigned.
                </Text>
              )}
            </Space>
          ),
          width: 520,
        });
        if (!dryRun) load();
      } else {
        message.error(response?.data?.message || "Run failed");
      }
    } catch (error) {
      message.error("Run failed");
    } finally {
      setRunning(false);
    }
  };

  const tiles = [
    { key: null, label: "All", value: data.total },
    ...STATUSES.map((s) => ({ key: s.value, label: s.label, value: data.counts?.[s.value] || 0 })),
  ];

  const columns = [
    {
      title: "Employee",
      key: "employee",
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.employee?.full_name || "-"}</Text>
          {row.employee?.emp_code && <Text type="secondary">{row.employee.emp_code}</Text>}
        </Space>
      ),
    },
    { title: "Form", dataIndex: "milestoneLabel", key: "milestone" },
    {
      title: "Due",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (value, row) => (
        <Space size={4}>
          {moment(value).format(dateFormat)}
          {row.isOverdue && <Tag color="red">Overdue</Tag>}
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => <Tag color={STATUS_COLOR[value]}>{value.replace(/_/g, " ")}</Tag>,
    },
    {
      title: "Submitted",
      dataIndex: "firstSubmittedAt",
      key: "submitted",
      render: (value) => (value ? moment(value).format("DD MMM, h:mm A") : "-"),
    },
    {
      title: "Decided by",
      key: "approver",
      render: (_, row) =>
        row.approvedAt ? (
          <Space direction="vertical" size={0}>
            <Text>{row.approverRole === "ADMIN" ? "Admin" : "Reporting Manager"}</Text>
            <Text type="secondary">{moment(row.approvedAt).format("DD MMM")}</Text>
          </Space>
        ) : (
          "-"
        ),
    },
    { title: "Template", dataIndex: "templateName", key: "template", ellipsis: true },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => (
        <Space wrap size={4}>
          <Tooltip title="Version history">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setHistory(row)}
              disabled={!row.submissionCount}
            />
          </Tooltip>
          <Tooltip title="Waive this milestone">
            <Button
              size="small"
              icon={<StopOutlined />}
              onClick={() => {
                setWaiving(row);
                setWaiveReason("");
              }}
              disabled={row.status === "WAIVED"}
            />
          </Tooltip>
          <Tooltip title="Reopen the replace window for 24h">
            <Popconfirm
              title="Reopen the replace window for 24 hours?"
              onConfirm={() => act(Service.reviewFormUnlockReplace, row, { hours: 24 })}
            >
              <Button size="small" icon={<UnlockOutlined />} />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Reopen a rejected form">
            <Popconfirm
              title="Reopen this form? It will be reassigned on the next run."
              onConfirm={() => act(Service.reviewFormReopen, row)}
            >
              <Button size="small" icon={<ReloadOutlined />} disabled={row.status !== "REJECTED"} />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Re-resolve the template">
            <Popconfirm
              title="Re-run template resolution for this form?"
              onConfirm={() => act(Service.reviewFormReresolveTemplate, row)}
            >
              <Button
                size="small"
                icon={<SyncOutlined />}
                disabled={row.status !== "PENDING_UPLOAD" || !!row.submissionCount}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="review-forms-page review-form-tracker">
      <Row gutter={[12, 12]} className="review-form-tiles">
        {tiles.map((tile) => (
          <Col xs={12} sm={8} md={4} key={tile.label}>
            <Card
              size="small"
              className={`review-form-tile ${filters.status === tile.key ? "is-active" : ""}`}
              onClick={() => {
                setFilters((f) => ({ ...f, status: tile.key }));
                setPagination((p) => ({ ...p, current: 1 }));
              }}
            >
              <Statistic title={tile.label} value={tile.value} />
            </Card>
          </Col>
        ))}
      </Row>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Employee name or code"
          style={{ width: 240 }}
          onSearch={(value) => {
            setFilters((f) => ({ ...f, search: value }));
            setPagination((p) => ({ ...p, current: 1 }));
          }}
        />
        <Select
          allowClear
          placeholder="All forms"
          style={{ width: 160 }}
          options={MILESTONES}
          value={filters.milestone}
          onChange={(value) => {
            setFilters((f) => ({ ...f, milestone: value || null }));
            setPagination((p) => ({ ...p, current: 1 }));
          }}
        />
        <Button loading={running} onClick={() => runAssignment(true)}>
          Dry run
        </Button>
        <Popconfirm
          title="Create every assignment that has come due for this company?"
          onConfirm={() => runAssignment(false)}
        >
          <Button type="primary" loading={running}>
            Run assignment now
          </Button>
        </Popconfirm>
      </Space>

      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={data.rows}
        scroll={{ x: "max-content" }}
        pagination={{
          ...TABLE_PAGINATION,
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data.total,
          onChange: (current, pageSize) => setPagination({ current, pageSize }),
        }}
      />

      <Modal
        title={`Version history: ${history?.milestoneLabel || ""}`}
        open={!!history}
        onCancel={() => setHistory(null)}
        footer={null}
      >
        <Text type="secondary">
          Every version is retained, including replaced ones. The live submission is the most
          recent entry that was not replaced.
        </Text>
        <Timeline style={{ marginTop: 16 }}>
          {history?.currentSubmission ? (
            <Timeline.Item color="green">
              <Space direction="vertical" size={0}>
                <Text strong>
                  <FilePdfOutlined /> {history.currentSubmission.originalName}
                </Text>
                <Text type="secondary">
                  {moment(history.currentSubmission.uploadedAt).format(`${dateFormat}, h:mm A`)} ·
                  current
                </Text>
              </Space>
            </Timeline.Item>
          ) : (
            <Timeline.Item color="gray">No live submission</Timeline.Item>
          )}
          {history?.firstSubmittedAt && (
            <Timeline.Item color="blue">
              First submitted {moment(history.firstSubmittedAt).format(`${dateFormat}, h:mm A`)}
              {history.replaceWindowEndsAt && (
                <div>
                  <Text type="secondary">
                    Replace window closed{" "}
                    {moment(history.replaceWindowEndsAt).format(`${dateFormat}, h:mm A`)}
                  </Text>
                </div>
              )}
            </Timeline.Item>
          )}
        </Timeline>
      </Modal>

      <Modal
        title={`Waive ${waiving?.milestoneLabel || ""}`}
        open={!!waiving}
        onCancel={() => setWaiving(null)}
        onOk={() => {
          act(Service.reviewFormWaive, waiving, { reason: waiveReason });
          setWaiving(null);
        }}
        okText="Waive"
      >
        <p>
          {waiving?.employee?.full_name} will no longer be asked to complete this form.
        </p>
        <TextArea
          rows={3}
          value={waiveReason}
          onChange={(e) => setWaiveReason(e.target.value)}
          placeholder="Reason (recorded in the activity log)"
        />
      </Modal>
    </div>
  );
};

export default ReviewFormTracker;
